"use client";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { LoaderCircle } from "lucide-react";
import { useState } from "react";
import { client } from "@/lib/hono";
import toast from "@/lib/toast";
import { useQueryClient } from "@tanstack/react-query";
import { useStorageQuery } from "../../hooks/storageQueryProvider";
import { t } from "@/lib/localization";
import PresetPicker from "@/features/storagePresets/components/presetPicker";

export default function FolderSettingsModal() {
    const { folderSettingsModalOpen, setFolderSettingsModalOpen } =
        useStorageQuery();

    return (
        <Dialog
            open={!!folderSettingsModalOpen}
            onOpenChange={(open) => {
                if (!open) setFolderSettingsModalOpen(null);
            }}
        >
            <DialogContent className="sm:max-w-[380px]">
                <DialogHeader>
                    <DialogTitle>
                        {t(
                            "storage.folder.settings-modal.header.title",
                            undefined,
                            undefined
                        )}
                    </DialogTitle>
                    <DialogDescription>
                        {t(
                            "storage.folder.settings-modal.header.description",
                            undefined,
                            undefined
                        )}
                    </DialogDescription>
                </DialogHeader>
                {folderSettingsModalOpen && (
                    <FolderSettingsForm
                        key={folderSettingsModalOpen.id}
                        folder={folderSettingsModalOpen}
                        onClose={() => setFolderSettingsModalOpen(null)}
                    />
                )}
            </DialogContent>
        </Dialog>
    );
}

function FolderSettingsForm({
    folder,
    onClose,
}: {
    folder: { id: string; name: string; presetId?: string | null };
    onClose: () => void;
}) {
    const [isPending, setIsPending] = useState(false);
    const [presetId, setPresetId] = useState<string | null>(
        folder.presetId ?? null
    );

    const queryClient = useQueryClient();

    const submitHandler = async (e: any) => {
        e.preventDefault();

        setIsPending(true);

        const newName = e.target.folderName.value;

        const res = await client.api.storage.folder[":folderId"].update.$post({
            param: {
                folderId: folder.id,
            },
            json: {
                name: newName,
                presetId,
            },
        });

        if (!res.ok) {
            const error = await res.text();
            console.log(error);
            toast.error(t("toast.error.update-folder"), {
                keyName: "toast.error.update-folder",
            });
            setIsPending(false);
        } else {
            queryClient.invalidateQueries({
                queryKey: ["get-storage-folders"],
            });
            queryClient.invalidateQueries({
                queryKey: ["storage-presets"],
            });

            toast.success(t("toast.success.update-folder"), {
                keyName: "toast.success.update-folder",
            });
            setIsPending(false);
            onClose();
        }
    };

    return (
        <form onSubmit={submitHandler}>
            <div className="flex flex-col gap-6 pb-4 pt-1">
                <div className="flex flex-col gap-1.5">
                    <Label htmlFor="folderName">
                        {t(
                            "storage.folder.settings-modal.name-input.label",
                            undefined,
                            undefined
                        )}
                    </Label>
                    <Input
                        id="folderName"
                        defaultValue={folder.name}
                        required
                    />
                </div>
                <div className="flex flex-col gap-1.5">
                    <Label>
                        {t("storage.folder.settings-modal.preset.label")}
                    </Label>
                    <PresetPicker value={presetId} onChange={setPresetId} />
                    <p className="text-xs text-muted-foreground">
                        {t("storage.folder.settings-modal.preset.hint")}
                    </p>
                </div>
            </div>
            <DialogFooter className="mt-4">
                <Button
                    disabled={isPending}
                    type="submit"
                    className="flex w-full justify-center items-center"
                >
                    {isPending ? (
                        <LoaderCircle className="size-4 text-white animate-spin" />
                    ) : (
                        t(
                            "storage.folder.settings-modal.save-btn",
                            undefined,
                            undefined
                        )
                    )}
                </Button>
            </DialogFooter>
        </form>
    );
}
